import { useState } from 'react'
import FileDrop from '../File/FileDrop'
import GuidedTour from '../Shared/GuidedTour'
import TicketTypePicker from '../TicketCreate/TicketTypePicker'
import ImpactUrgencyPicker from '../TicketCreate/ImpactUrgencyPicker'
import OriginPicker from '../TicketCreate/OriginPicker'
import { useApp } from '../../context/AppContext'
import useTicketDraft from '../../hooks/useTicketDraft'
import useCreateTicket from '../../hooks/useCreateTicket'
import useTicketPriorities from '../../hooks/useTicketPriorities'
import { MIN_DESCRIPTION_LENGTH } from '../../utils/ticketDraft'
import { ADVANCED_TOUR_STEPS } from '../../utils/ticketCreateFlows'
import { employeeName } from '../../utils/employee'
import '../../styles/ticket-form.css'

/**
 * Formulario avanzado de creación de tickets: todos los campos en una vista,
 * con un tutorial guiado opcional.
 *
 * @component
 * @returns {JSX.Element}
 */
const AddTicket = () => {
  const { employee } = useApp()
  const { draft, errors, setField, patch, validate, reset } = useTicketDraft()
  const { submit, saving, uploading } = useCreateTicket()
  const { priorityLabel } = useTicketPriorities()
  const [files, setFiles] = useState([])
  const [tourOpen, setTourOpen] = useState(false)

  const isBlocked = saving || uploading
  const author = employeeName(employee) || 'Usuario'

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    await submit(draft, files)
  }

  const handleDiscard = () => {
    reset()
    setFiles([])
  }

  return (
    <div className="at-page">
      {uploading && (
        <div className="at-overlay">
          <div className="at-overlay-card">
            <div
              className="spinner-border text-primary"
              style={{ width: '2rem', height: '2rem' }}
              role="status"
            >
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mb-0 text-body-secondary small">
              Subiendo archivos, por favor espere…
            </p>
          </div>
        </div>
      )}

      {tourOpen && (
        <GuidedTour
          steps={ADVANCED_TOUR_STEPS}
          onClose={() => setTourOpen(false)}
        />
      )}

      <form onSubmit={handleSubmit} className="at-form" noValidate>
        <div className="at-tour-bar">
          <span>
            <i className="fa-regular fa-lightbulb me-2" />
            ¿Primera vez con este formulario?
          </span>
          <button
            type="button"
            className="btn btn-sm at-tour-btn"
            onClick={() => setTourOpen(true)}
          >
            <i className="fa-solid fa-circle-play me-2" />
            Ver tutorial
          </button>
        </div>

        {/* ── INFORMACIÓN ── */}
        <section className="at-card">
          <div className="at-card-head">
            <span className="at-step">1</span>
            <span>Información</span>
          </div>

          <div className="row g-3">
            <div className="col-12" data-tour="title">
              <label className="at-label" htmlFor="at-title">
                Título <span className="at-req">*</span>
                <span className="at-label-q">Resuma el caso en una línea</span>
              </label>
              <input
                id="at-title"
                type="text"
                className={`form-control at-input ${errors.title ? 'is-invalid' : ''}`}
                placeholder="Ej: Error crítico en la pasarela de pagos"
                value={draft.title}
                maxLength={150}
                onChange={(e) => setField('title', e.target.value)}
              />
              {errors.title && <p className="at-error-text">{errors.title}</p>}
            </div>

            <div className="col-md-6" data-tour="origin">
              <label className="at-label">
                Plataforma / Origen <span className="at-req">*</span>
              </label>
              <OriginPicker
                value={draft.systemOrigin}
                onChange={(value) => setField('systemOrigin', value)}
                invalid={!!errors.systemOrigin}
              />
              {errors.systemOrigin && (
                <p className="at-error-text">{errors.systemOrigin}</p>
              )}
            </div>

            <div className="col-md-6">
              <label className="at-label">Autor</label>
              <div className="at-author">
                <span className="at-author-avatar">
                  {author[0].toUpperCase()}
                </span>
                <span className="at-author-name">{author}</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── CLASIFICACIÓN ── */}
        <section className="at-card">
          <div className="at-card-head">
            <span className="at-step">2</span>
            <span>Clasificación</span>
          </div>

          <div data-tour="type">
            <label className="at-label">Tipo de registro</label>
            <TicketTypePicker
              value={draft.type}
              onChange={(value) => setField('type', value)}
            />
          </div>

          <div className="mt-3" data-tour="priority">
            <ImpactUrgencyPicker
              impact={draft.impact}
              urgency={draft.urgency}
              priorityId={draft.priorityId}
              priorityLabel={priorityLabel}
              onChange={patch}
            />
          </div>
        </section>

        {/* ── DETALLE ── */}
        <section className="at-card">
          <div className="at-card-head">
            <span className="at-step">3</span>
            <span>Descripción y adjuntos</span>
          </div>

          <div data-tour="description">
            <label className="at-label" htmlFor="at-description">
              Descripción detallada <span className="at-req">*</span>
            </label>
            <textarea
              id="at-description"
              className={`form-control at-input ${errors.description ? 'is-invalid' : ''}`}
              rows="5"
              placeholder={
                '¿Qué sucedió?\n¿Qué pasos hay que seguir para reproducirlo?\n¿Qué esperaba que ocurriera?'
              }
              value={draft.description}
              onChange={(e) => setField('description', e.target.value)}
            />
            {errors.description && (
              <p className="at-error-text">{errors.description}</p>
            )}
            <div className="at-field-foot">
              <span>
                Incluya el código de proyecto, el usuario afectado y la hora
                aproximada.
              </span>
              <span
                className={
                  draft.description.length < MIN_DESCRIPTION_LENGTH
                    ? 'at-count-low'
                    : ''
                }
              >
                {draft.description.length} caracteres
              </span>
            </div>
          </div>

          <div className="mt-3" data-tour="files">
            <FileDrop files={files} setFiles={setFiles} />
          </div>
        </section>

        {/* ACCIONES */}
        <div className="at-actions">
          <button
            type="button"
            className="btn at-btn-ghost"
            onClick={handleDiscard}
            disabled={isBlocked}
          >
            Descartar
          </button>
          <button
            type="submit"
            className="btn at-btn-primary"
            disabled={isBlocked}
            data-tour="submit"
          >
            {isBlocked ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {uploading ? 'Subiendo archivos…' : 'Guardando…'}
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane me-2" />
                Crear Ticket
              </>
            )}
          </button>
        </div>
      </form>

      <style>{`
        .at-tour-bar {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          padding: 10px 14px; border-radius: 12px; font-size: 0.85rem;
          border: 1px dashed var(--gm-border); color: var(--gm-text-muted);
        }
        .at-tour-btn {
          flex-shrink: 0; border-radius: 9px; font-weight: 600;
          color: var(--gm-primary); background: var(--gm-primary-soft);
        }
        .at-tour-btn:hover { color: #fff; background: var(--gm-primary); }
      `}</style>
    </div>
  )
}

export default AddTicket
